import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Leaf, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Reset link sent! Check your email.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Card className="border-0 shadow-lg" data-testid="forgot-password-card">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-praroha-sage/20 mb-4">
                <Leaf className="w-6 h-6 text-praroha-forest" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-praroha-forest">Forgot Password</h1>
              <p className="text-muted-foreground text-sm mt-1">Enter your email and we'll send you a reset link</p>
            </div>

            {sent ? (
              <div className="text-center py-4" data-testid="reset-email-sent">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 mb-4">
                  <Mail className="w-8 h-8 text-praroha-forest" />
                </div>
                <h2 className="text-lg font-semibold text-praroha-forest mb-2">Check Your Email</h2>
                <p className="text-muted-foreground text-sm mb-6">We've sent a password reset link to <strong>{email}</strong>. The link expires in 1 hour.</p>
                <Link to="/login" className="text-sm text-praroha-sage hover:underline">Back to Login</Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-praroha-forest hover:bg-praroha-forest/90" data-testid="forgot-submit-btn">
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
                <div className="text-center">
                  <Link to="/login" className="inline-flex items-center gap-1 text-sm text-praroha-sage hover:underline">
                    <ArrowLeft className="w-3 h-3" /> Back to Login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
